import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { useUsers } from '@/hooks/use-users';
import type { WorkGroupFolderPermissionLevel } from '@/lib/api';
import {
  useDeleteWorkGroupFolderPermission,
  useUpsertWorkGroupFolderPermission,
  useWorkGroupFolderPermissions,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';
import { WorkGroupInternalUserPicker } from '@/features/workgroups/components/work-group-internal-user-picker';

const LEVELS: WorkGroupFolderPermissionLevel[] = ['none', 'read', 'write', 'manage'];

interface WorkGroupFolderPermissionsDialogProps {
  workGroupId: string;
  folderId: string;
  folderName: string;
  privateAccessReason?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkGroupFolderPermissionsDialog({
  workGroupId,
  folderId,
  folderName,
  privateAccessReason,
  open,
  onOpenChange,
}: WorkGroupFolderPermissionsDialogProps) {
  const { hasCapability } = usePermissions();
  const canManage = hasCapability('workgroups.folder.permissions.manage');

  const { data: permissions, isLoading, error } = useWorkGroupFolderPermissions(
    workGroupId,
    folderId,
    privateAccessReason,
  );
  const {
    data: users = [],
    isLoading: usersLoading,
    isError: usersError,
  } = useUsers();

  const upsertMutation = useUpsertWorkGroupFolderPermission();
  const deleteMutation = useDeleteWorkGroupFolderPermission();

  const [selectedUserId, setSelectedUserId] = useState('');
  const [newLevel, setNewLevel] = useState<WorkGroupFolderPermissionLevel>('read');

  const assignedIds = useMemo(
    () => new Set(permissions?.map((p) => p.userId) ?? []),
    [permissions],
  );

  const onError = (err: unknown) => {
    toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
  };

  const handleAdd = () => {
    if (!selectedUserId) {
      toast({ title: 'Select an internal user', variant: 'destructive' });
      return;
    }
    upsertMutation.mutate(
      { workGroupId, folderId, userId: selectedUserId, level: newLevel, privateAccessReason },
      {
        onSuccess: () => {
          toast({ title: 'Permission saved' });
          setSelectedUserId('');
        },
        onError,
      },
    );
  };

  const handleLevelChange = (userId: string, level: WorkGroupFolderPermissionLevel) => {
    upsertMutation.mutate(
      { workGroupId, folderId, userId, level, privateAccessReason },
      {
        onSuccess: () => toast({ title: 'Permission updated' }),
        onError,
      },
    );
  };

  const handleDelete = (userId: string) => {
    deleteMutation.mutate(
      { workGroupId, folderId, userId, privateAccessReason },
      {
        onSuccess: () => toast({ title: 'Permission removed' }),
        onError,
      },
    );
  };

  if (!canManage) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="wg-folder-permissions-dialog">
        <DialogHeader>
          <DialogTitle>Folder permissions</DialogTitle>
          <p className="text-sm text-muted-foreground truncate">{folderName}</p>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Only explicit overrides are listed here. Users without an entry inherit access from parent
          folders or the work group default. Removing an override restores inheritance for that user.
        </p>

        {isLoading ? <p className="text-sm text-muted-foreground">Loading permissions…</p> : null}
        {error ? (
          <p className="text-sm text-destructive">Could not load folder permissions.</p>
        ) : null}

        {permissions && permissions.length === 0 && !isLoading ? (
          <p className="text-sm text-muted-foreground">No explicit overrides for this folder.</p>
        ) : null}

        {permissions && permissions.length > 0 ? (
          <ul className="max-h-48 space-y-2 overflow-auto">
            {permissions.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-foreground">
                    {row.user.displayName?.trim() || row.user.email}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{row.user.email}</p>
                </div>
                <Select
                  value={row.level}
                  onValueChange={(v) =>
                    handleLevelChange(row.userId, v as WorkGroupFolderPermissionLevel)
                  }
                >
                  <SelectTrigger className="h-8 w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((level) => (
                      <SelectItem key={level} value={level}>
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => handleDelete(row.userId)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="space-y-3 border-t border-border pt-3">
          <p className="text-sm font-medium text-foreground">Add override</p>
          <WorkGroupInternalUserPicker
            id="wg-perm-user"
            label="Internal user"
            value={selectedUserId}
            onValueChange={setSelectedUserId}
            users={users}
            excludedUserIds={assignedIds}
            isLoading={usersLoading}
            loadError={usersError}
            disabled={upsertMutation.isPending}
            data-testid="wg-folder-perm-user-picker"
          />
          <div className="space-y-2">
            <Label htmlFor="wg-perm-level">Level</Label>
            <Select
              value={newLevel}
              onValueChange={(v) => setNewLevel(v as WorkGroupFolderPermissionLevel)}
            >
              <SelectTrigger id="wg-perm-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEVELS.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            disabled={upsertMutation.isPending || usersLoading || !selectedUserId}
            onClick={handleAdd}
          >
            Save permission
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
